/**
 * Number-word ↔ digit normalization (English + French, 0–100).
 *
 * Returns query variants so the search engine can try both "two" and "2"
 * against catalog data that may use either form.
 */

const ENGLISH_NUMBERS: Record<string, string> = {
  zero: "0",
  one: "1",
  two: "2",
  three: "3",
  four: "4",
  five: "5",
  six: "6",
  seven: "7",
  eight: "8",
  nine: "9",
  ten: "10",
  eleven: "11",
  twelve: "12",
  thirteen: "13",
  fourteen: "14",
  fifteen: "15",
  sixteen: "16",
  seventeen: "17",
  eighteen: "18",
  nineteen: "19",
  twenty: "20",
  thirty: "30",
  forty: "40",
  fifty: "50",
  sixty: "60",
  seventy: "70",
  eighty: "80",
  ninety: "90",
  hundred: "100",
}

const FRENCH_NUMBERS: Record<string, string> = {
  zéro: "0",
  un: "1",
  deux: "2",
  trois: "3",
  quatre: "4",
  cinq: "5",
  six: "6",
  sept: "7",
  huit: "8",
  neuf: "9",
  dix: "10",
  onze: "11",
  douze: "12",
  treize: "13",
  quatorze: "14",
  quinze: "15",
  seize: "16",
  "dix-sept": "17",
  "dix-huit": "18",
  "dix-neuf": "19",
  vingt: "20",
  trente: "30",
  quarante: "40",
  cinquante: "50",
  soixante: "60",
  "soixante-dix": "70",
  "quatre-vingts": "80",
  "quatre-vingt-dix": "90",
  cent: "100",
}

const NUMBER_TO_ENGLISH: Record<string, string> = {}
const NUMBER_TO_FRENCH: Record<string, string> = {}
for (const [word, num] of Object.entries(ENGLISH_NUMBERS)) {
  NUMBER_TO_ENGLISH[num] = word
}
for (const [word, num] of Object.entries(FRENCH_NUMBERS)) {
  NUMBER_TO_FRENCH[num] = word
}

function parseCompoundNumber(text: string): string | null {
  const englishCompound = text.match(
    /^(twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)[-\s]?(one|two|three|four|five|six|seven|eight|nine)$/i
  )
  if (englishCompound) {
    const tens = ENGLISH_NUMBERS[englishCompound[1].toLowerCase()]
    const ones = ENGLISH_NUMBERS[englishCompound[2].toLowerCase()]
    return String(parseInt(tens) + parseInt(ones))
  }

  const frenchCompound = text.match(
    /^(vingt|trente|quarante|cinquante|soixante)\s*(?:et\s*)?(un|deux|trois|quatre|cinq|six|sept|huit|neuf)$/i
  )
  if (frenchCompound) {
    const tens = FRENCH_NUMBERS[frenchCompound[1].toLowerCase()]
    const ones = FRENCH_NUMBERS[frenchCompound[2].toLowerCase()]
    return String(parseInt(tens) + parseInt(ones))
  }

  return null
}

/**
 * Expand a query into all equivalent variants by translating number words
 * to/from digits. Always includes the original.
 */
export function normalizeNumbersInQuery(query: string): string[] {
  const normalized = query.toLowerCase()
  const variants: string[] = [query]

  const englishCompound = normalized.match(
    /\b(twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)[-\s]?(one|two|three|four|five|six|seven|eight|nine)\b/i
  )
  if (englishCompound) {
    const compound = parseCompoundNumber(englishCompound[0])
    if (compound) {
      variants.push(
        query.replace(
          new RegExp(englishCompound[0].replace(/\s+/g, "\\s+"), "gi"),
          compound
        )
      )
    }
  }

  const frenchCompound = normalized.match(
    /\b(vingt|trente|quarante|cinquante|soixante)\s*(?:et\s*)?(un|deux|trois|quatre|cinq|six|sept|huit|neuf)\b/i
  )
  if (frenchCompound) {
    const compound = parseCompoundNumber(frenchCompound[0])
    if (compound) {
      variants.push(
        query.replace(
          new RegExp(frenchCompound[0].replace(/\s+/g, "\\s+"), "gi"),
          compound
        )
      )
    }
  }

  for (const [word, num] of Object.entries(ENGLISH_NUMBERS)) {
    if (normalized.includes(word)) {
      variants.push(query.replace(new RegExp(`\\b${word}\\b`, "gi"), num))
    }
  }

  for (const [word, num] of Object.entries(FRENCH_NUMBERS)) {
    if (normalized.includes(word)) {
      variants.push(query.replace(new RegExp(`\\b${word}\\b`, "gi"), num))
    }
  }

  const digitMatch = normalized.match(/\b(\d{1,3})\b/)
  if (digitMatch) {
    const num = digitMatch[1]
    if (NUMBER_TO_ENGLISH[num]) {
      variants.push(
        query.replace(new RegExp(`\\b${num}\\b`, "g"), NUMBER_TO_ENGLISH[num])
      )
    }
    if (NUMBER_TO_FRENCH[num]) {
      variants.push(
        query.replace(new RegExp(`\\b${num}\\b`, "g"), NUMBER_TO_FRENCH[num])
      )
    }
  }

  return Array.from(new Set(variants))
}

/** Expand a single word/number to all known variants. */
export function expandNumberVariants(input: string): string[] {
  const variants: string[] = [input]
  const lower = input.toLowerCase()

  if (/^\d+$/.test(input)) {
    if (NUMBER_TO_ENGLISH[input]) variants.push(NUMBER_TO_ENGLISH[input])
    if (NUMBER_TO_FRENCH[input]) variants.push(NUMBER_TO_FRENCH[input])
  }

  if (ENGLISH_NUMBERS[lower]) variants.push(ENGLISH_NUMBERS[lower])
  if (FRENCH_NUMBERS[lower]) variants.push(FRENCH_NUMBERS[lower])

  return Array.from(new Set(variants))
}
